import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  useJobs,
  useDismissJobNoGo,
  useDismissJobsNoGoBulk,
  useRestoreJobToInbox,
  useRestoreJobsToInboxBulk,
} from "@/hooks/useJobs";
import { usePipelines, useCreatePipeline } from "@/hooks/usePipeline";
import { useClientPagination } from "@/hooks/usePagination";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useSyncCursorPage } from "@/hooks/useSyncCursorPage";
import { PaginationBar } from "@/components/pagination-bar";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { getPipelineForJob } from "@/lib/pipeline-analytics";
import { resolveJobScore, scoreTone } from "@/format";
import { cn } from "@/cn";
import { decodeHtmlEntities } from "@/lib/html-entities";
import {
  CollectionCommandBar,
  DataTable,
  EntityCard,
  EntityCompactItem,
  EntityListLayout,
  useClientTableSort,
  useListViewMode,
  type Column,
  type EntityFilterSelectField,
  type EntitySortOption,
} from "@/components/collection";
import {
  type JobsTab,
  type JobSortKey,
  type SourceFilter,
  SOURCE_FILTER_OPTIONS,
  isInboxJob,
  isNoGoJob,
  isInPipelineJob,
  sortJobs,
  matchesSourceFilter,
  sourceBadgeClass,
  sourceLabel,
  jobAddedAt,
} from "@/lib/job-ui";
import { JOB_BUCKET_GLOSSARY } from "@/lib/job-bucket-glossary";
import {
  type TitleFitTag,
  TITLE_FIT_FILTER_OPTIONS,
  matchesTitleFitFilter,
} from "@/lib/job-title-fit";
import { parseNoGoPipelineConflict } from "@/lib/no-go-error";
import type { Job } from "@career-ops-amplify/shared";

const TONE_BG: Record<string, string> = {
  good: "bg-emerald-500/10 text-emerald-400",
  warn: "bg-amber-500/10 text-amber-400",
  bad: "bg-red-500/10 text-red-400",
  muted: "bg-zinc-500/10 text-muted-foreground",
};

const JOB_SORT_OPTIONS: EntitySortOption<JobSortKey>[] = [
  { field: "newest", order: "desc", label: "Date added (newest)" },
  { field: "oldest", order: "asc", label: "Date added (oldest)" },
  { field: "company", order: "asc", label: "Company A–Z" },
  { field: "source", order: "asc", label: "Source" },
];

const ALLOWED_MODES = ["table", "list", "cards"] as const;
const BULK_START_CAP = 50;
const UNDO_MS = 15_000;

const TAB_GLOSSARY: Record<JobsTab, keyof typeof JOB_BUCKET_GLOSSARY> = {
  inbox: "inbox",
  pipeline: "pipeline",
  nogo: "nogo",
  all: "inbox",
};

function displayCompany(job: Job): string {
  return decodeHtmlEntities(job.company ?? "");
}

function displayRole(job: Job): string {
  return decodeHtmlEntities(job.role ?? "");
}

function pipelineHref(pipelineId: string, stage?: string): string {
  const base = `/pipeline/${pipelineId}`;
  return stage ? `${base}?stage=${encodeURIComponent(stage)}` : base;
}

function parseTab(value: string | null): JobsTab {
  if (value === "pipeline" || value === "nogo" || value === "all") return value;
  return "inbox";
}

function parseTitleFit(value: string | null): TitleFitTag | "" {
  if (
    value === "cto_like" ||
    value === "commercial" ||
    value === "ic_engineer" ||
    value === "sales" ||
    value === "other"
  ) {
    return value;
  }
  return "";
}

function sortOptionFromKey(key: JobSortKey): EntitySortOption<JobSortKey> {
  return JOB_SORT_OPTIONS.find((o) => o.field === key) ?? JOB_SORT_OPTIONS[0];
}

function tabAllowsSelect(tab: JobsTab): boolean {
  return tab === "inbox" || tab === "nogo" || tab === "all";
}

export function JobsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>(
    (searchParams.get("source") as SourceFilter) || "",
  );
  const [titleFitFilter, setTitleFitFilter] = useState<TitleFitTag | "">(
    parseTitleFit(searchParams.get("titleFit")),
  );
  const [sortOption, setSortOption] = useState<EntitySortOption<JobSortKey>>(
    sortOptionFromKey((searchParams.get("sort") as JobSortKey) || "newest"),
  );
  const activeTab = parseTab(searchParams.get("tab"));

  const { viewMode, setViewMode, allowedModes } = useListViewMode({
    storageKey: "viewMode_jobs",
    defaultMode: "table",
    allowedModes: [...ALLOWED_MODES],
  });

  const setActiveTab = (tab: JobsTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === "inbox") next.delete("tab");
      else next.set("tab", tab);
      return next;
    });
  };

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (sourceFilter) next.set("source", sourceFilter);
      else next.delete("source");
      if (titleFitFilter) next.set("titleFit", titleFitFilter);
      else next.delete("titleFit");
      if (sortOption.field !== "newest") next.set("sort", sortOption.field);
      else next.delete("sort");
      return next;
    });
  }, [sourceFilter, titleFitFilter, sortOption.field, setSearchParams]);

  const useServerCursor =
    activeTab === "all" && !search.trim() && !sourceFilter && !titleFitFilter;

  const cursorPag = useCursorPagination([activeTab]);

  const { data: jobsData, isLoading: jobsLoading } = useJobs();
  const {
    data: cursorJobsData,
    isLoading: cursorJobsLoading,
  } = useJobs(
    useServerCursor
      ? { limit: cursorPag.pageSize, cursor: cursorPag.cursor }
      : undefined,
  );
  useSyncCursorPage(
    cursorPag.setNextCursor,
    cursorJobsData,
    useServerCursor && !cursorJobsLoading && !!cursorJobsData,
  );

  const { data: pipelinesData, isLoading: pipelinesLoading } = usePipelines();
  const createPipelineMutation = useCreatePipeline();
  const dismissMutation = useDismissJobNoGo();
  const bulkDismissMutation = useDismissJobsNoGoBulk();
  const restoreMutation = useRestoreJobToInbox();
  const bulkRestoreMutation = useRestoreJobsToInboxBulk();

  const [creatingJobId, setCreatingJobId] = useState<string | null>(null);
  const [dismissingJobId, setDismissingJobId] = useState<string | null>(null);
  const [restoringJobId, setRestoringJobId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmNoGoJobId, setConfirmNoGoJobId] = useState<string | null>(null);
  const [confirmBulkNoGoOpen, setConfirmBulkNoGoOpen] = useState(false);
  const [confirmBulkStartOpen, setConfirmBulkStartOpen] = useState(false);
  const [confirmBulkRestoreOpen, setConfirmBulkRestoreOpen] = useState(false);
  const [confirmNotMatchingOpen, setConfirmNotMatchingOpen] = useState(false);
  const [bulkStartBusy, setBulkStartBusy] = useState(false);
  const [actionError, setActionError] = useState<{
    message: string;
    pipelineId?: string;
  } | null>(null);
  const [bulkSummary, setBulkSummary] = useState<string | null>(null);
  const [undoNoGoIds, setUndoNoGoIds] = useState<string[] | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const jobs = jobsData?.jobs ?? [];
  const pipelines = pipelinesData?.pipelines ?? [];
  const isLoading =
    pipelinesLoading ||
    jobsLoading ||
    (useServerCursor && cursorJobsLoading);

  const inboxJobs = useMemo(() => jobs.filter(isInboxJob), [jobs]);
  const pipelineJobs = useMemo(
    () => jobs.filter((j) => isInPipelineJob(j, pipelines)),
    [jobs, pipelines],
  );
  const noGoJobs = useMemo(() => jobs.filter(isNoGoJob), [jobs]);

  const notMatchingTitleFitIds = useMemo(() => {
    if (!titleFitFilter || activeTab !== "inbox") return [];
    return inboxJobs
      .filter((j) => !matchesTitleFitFilter(j.role, titleFitFilter))
      .map((j) => j.id);
  }, [titleFitFilter, activeTab, inboxJobs]);

  const filteredJobs = useMemo(() => {
    if (useServerCursor) {
      return sortJobs(cursorJobsData?.jobs ?? [], sortOption.field);
    }

    let list =
      activeTab === "inbox"
        ? inboxJobs
        : activeTab === "pipeline"
          ? pipelineJobs
          : activeTab === "nogo"
            ? noGoJobs
            : jobs;

    if (sourceFilter) {
      list = list.filter((j) => matchesSourceFilter(j, sourceFilter));
    }
    if (titleFitFilter) {
      list = list.filter((j) => matchesTitleFitFilter(j.role, titleFitFilter));
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (j) =>
          displayCompany(j).toLowerCase().includes(q) ||
          displayRole(j).toLowerCase().includes(q),
      );
    }

    return sortJobs(list, sortOption.field);
  }, [
    useServerCursor,
    cursorJobsData?.jobs,
    activeTab,
    inboxJobs,
    pipelineJobs,
    noGoJobs,
    jobs,
    sourceFilter,
    titleFitFilter,
    search,
    sortOption.field,
  ]);

  const clientPag = useClientPagination(
    useServerCursor ? [] : filteredJobs,
    [activeTab, sourceFilter, titleFitFilter, search, sortOption.field],
  );

  const pagedJobs = useServerCursor ? filteredJobs : clientPag.pageItems;
  const getSortValue = useCallback((job: Job, key: string) => {
    if (key === "role") return displayRole(job);
    if (key === "source") return `${job.source ?? ""}:${job.sourceDetail ?? ""}`;
    if (key === "score") return resolveJobScore(job) ?? -1;
    if (key === "added") {
      return new Date(
        activeTab === "nogo" ? job.updatedAt : jobAddedAt(job),
      ).getTime();
    }
    return displayCompany(job);
  }, [activeTab]);

  const { sortedItems, sortKey, sortDirection, onSortChange } =
    useClientTableSort({
      items: pagedJobs,
      defaultKey: null,
      getValue: getSortValue,
    });

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab, sourceFilter, titleFitFilter, search]);

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  const clearUndoWindow = () => {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    setUndoNoGoIds(null);
  };

  const openUndoWindow = (ids: string[]) => {
    clearUndoWindow();
    if (ids.length === 0) return;
    setUndoNoGoIds(ids);
    undoTimerRef.current = setTimeout(() => {
      setUndoNoGoIds(null);
      undoTimerRef.current = null;
    }, UNDO_MS);
  };

  const toggleSelect = (jobId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const allPagedSelected =
    pagedJobs.length > 0 && pagedJobs.every((j) => selectedIds.has(j.id));

  const toggleSelectAllPaged = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPagedSelected) {
        for (const j of pagedJobs) next.delete(j.id);
      } else {
        for (const j of pagedJobs) next.add(j.id);
      }
      return next;
    });
  };

  const handleStartPipeline = (jobId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCreatingJobId(jobId);
    createPipelineMutation.mutate(jobId, {
      onSuccess: (data) => {
        navigate(pipelineHref(data.pipeline.id, "Evaluated"));
        setCreatingJobId(null);
      },
      onError: () => setCreatingJobId(null),
    });
  };

  const runNoGo = (jobId: string) => {
    setDismissingJobId(jobId);
    setActionError(null);
    dismissMutation.mutate(
      { jobId },
      {
        onError: (err) => {
          const conflict = parseNoGoPipelineConflict(err);
          if (conflict) setActionError(conflict);
          else
            setActionError({
              message: err instanceof Error ? err.message : "No-go failed",
            });
        },
        onSettled: () => setDismissingJobId(null),
      },
    );
  };

  const handleNoGoClick = (jobId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmNoGoJobId(jobId);
  };

  const handleRestore = (jobId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRestoringJobId(jobId);
    restoreMutation.mutate(jobId, {
      onSettled: () => setRestoringJobId(null),
    });
  };

  const finishBulkNoGo = (ids: string[]) => {
    if (ids.length === 0) return;
    setBulkSummary(null);
    setActionError(null);
    bulkDismissMutation.mutate(
      { jobIds: ids },
      {
        onSuccess: (result) => {
          setSelectedIds(new Set());
          setConfirmBulkNoGoOpen(false);
          setConfirmNotMatchingOpen(false);
          const parts = [`Marked ${result.counts.ok} no-go`];
          if (result.counts.skippedPiped)
            parts.push(
              `${result.counts.skippedPiped} already in pipeline (skipped)`,
            );
          if (result.counts.missing)
            parts.push(`${result.counts.missing} missing`);
          if (result.counts.failed) parts.push(`${result.counts.failed} failed`);
          setBulkSummary(parts.join(" · "));
          openUndoWindow(result.ok);
        },
        onError: (err) => {
          setActionError({
            message: err instanceof Error ? err.message : "Bulk no-go failed",
          });
          setConfirmBulkNoGoOpen(false);
          setConfirmNotMatchingOpen(false);
        },
      },
    );
  };

  const runBulkNoGoSelected = () => {
    finishBulkNoGo([...selectedIds]);
  };

  const runBulkNoGoNotMatching = () => {
    finishBulkNoGo(notMatchingTitleFitIds);
  };

  const runBulkRestore = () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkSummary(null);
    bulkRestoreMutation.mutate(ids, {
      onSuccess: (result) => {
        setSelectedIds(new Set());
        setConfirmBulkRestoreOpen(false);
        setBulkSummary(`Restored ${result.counts.ok} to inbox`);
      },
      onError: (err) => {
        setActionError({
          message: err instanceof Error ? err.message : "Bulk restore failed",
        });
        setConfirmBulkRestoreOpen(false);
      },
    });
  };

  const runBulkStart = async () => {
    const candidates = jobs.filter(
      (j) => selectedIds.has(j.id) && isInboxJob(j),
    );
    const capped = candidates.slice(0, BULK_START_CAP);
    if (capped.length === 0) {
      setConfirmBulkStartOpen(false);
      return;
    }
    setBulkStartBusy(true);
    setBulkSummary(null);
    let started = 0;
    let failed = 0;
    for (const job of capped) {
      try {
        await createPipelineMutation.mutateAsync(job.id);
        started += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkStartBusy(false);
    setConfirmBulkStartOpen(false);
    setSelectedIds(new Set());
    const parts = [`Started ${started} pipeline${started === 1 ? "" : "s"}`];
    if (failed) parts.push(`${failed} failed`);
    if (candidates.length > BULK_START_CAP) {
      parts.push(`capped at ${BULK_START_CAP}`);
    }
    setBulkSummary(parts.join(" · "));
  };

  const runUndoBulkNoGo = () => {
    if (!undoNoGoIds?.length) return;
    const ids = [...undoNoGoIds];
    clearUndoWindow();
    bulkRestoreMutation.mutate(ids, {
      onSuccess: (result) => {
        setBulkSummary(`Undid no-go for ${result.counts.ok} job(s)`);
      },
      onError: (err) => {
        setActionError({
          message: err instanceof Error ? err.message : "Undo failed",
        });
      },
    });
  };

  const selectedInboxCount = useMemo(
    () =>
      [...selectedIds].filter((id) => {
        const j = jobs.find((x) => x.id === id);
        return j && isInboxJob(j);
      }).length,
    [selectedIds, jobs],
  );

  const tabs: Array<{ id: JobsTab; label: string; count: number }> = [
    { id: "inbox", label: "Inbox", count: inboxJobs.length },
    { id: "pipeline", label: "In pipeline", count: pipelineJobs.length },
    { id: "nogo", label: "No-go", count: noGoJobs.length },
    { id: "all", label: "All", count: jobs.length },
  ];

  const filterFields: EntityFilterSelectField[] = [
    {
      id: "source",
      label: "Source",
      value: sourceFilter,
      allLabel: "All sources",
      options: SOURCE_FILTER_OPTIONS.filter((o) => o.value).map((o) => ({
        value: o.value,
        label: o.label,
      })),
      onChange: (v) => setSourceFilter(v as SourceFilter),
    },
    {
      id: "titleFit",
      label: "Title fit",
      value: titleFitFilter,
      allLabel: "All title fits",
      options: TITLE_FIT_FILTER_OPTIONS.filter((o) => o.value).map((o) => ({
        value: o.value,
        label: o.label,
      })),
      onChange: (v) => setTitleFitFilter(parseTitleFit(v || null)),
    },
  ];

  const renderSelectCheckbox = (job: Job) => (
    <input
      type="checkbox"
      data-testid="jobs-row-checkbox"
      checked={selectedIds.has(job.id)}
      onChange={() => toggleSelect(job.id)}
      onClick={(e) => e.stopPropagation()}
      aria-label={`Select ${displayCompany(job)}`}
      className="h-4 w-4 accent-brand"
    />
  );

  const renderActions = (job: Job) => {
    const pipeline = getPipelineForJob(job, pipelines);
    const stage = pipeline?.currentStage;
    const pipelineId = job.pipelineId ?? pipeline?.pipelineId ?? pipeline?.id;

    if (activeTab === "inbox") {
      return (
        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            data-testid="jobs-inbox-nogo"
            onClick={(e) => handleNoGoClick(job.id, e)}
            disabled={dismissingJobId === job.id}
            className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/20 disabled:opacity-50"
          >
            {dismissingJobId === job.id ? "…" : "No-go"}
          </button>
          <button
            type="button"
            data-testid="jobs-inbox-start"
            onClick={(e) => handleStartPipeline(job.id, e)}
            disabled={creatingJobId === job.id}
            className="rounded-md bg-brand px-2.5 py-1 text-xs font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
          >
            {creatingJobId === job.id ? "…" : "Start"}
          </button>
        </div>
      );
    }
    if (activeTab === "nogo") {
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={(e) => handleRestore(job.id, e)}
            disabled={restoringJobId === job.id}
            className="rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted/20 disabled:opacity-50"
          >
            {restoringJobId === job.id ? "…" : "Restore"}
          </button>
        </div>
      );
    }
    if (activeTab === "pipeline" && pipelineId) {
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <Link
            to={pipelineHref(pipelineId, stage)}
            className="rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted/20"
          >
            Open pipeline
          </Link>
        </div>
      );
    }
    if (activeTab === "all") {
      if (pipelineId) {
        return (
          <div
            className="flex justify-end gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="jobs-all-nogo-piped"
              onClick={(e) => handleNoGoClick(job.id, e)}
              disabled={dismissingJobId === job.id}
              className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/20 disabled:opacity-50"
              title="Triggers Discard-in-Pipeline CTA when job already has a pipeline"
            >
              {dismissingJobId === job.id ? "…" : "No-go"}
            </button>
            <Link
              to={pipelineHref(pipelineId, stage)}
              className="rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted/20"
            >
              Open pipeline
            </Link>
          </div>
        );
      }
      if (isInboxJob(job)) {
        return (
          <div
            className="flex justify-end gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="jobs-all-nogo"
              onClick={(e) => handleNoGoClick(job.id, e)}
              disabled={dismissingJobId === job.id}
              className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/20 disabled:opacity-50"
            >
              {dismissingJobId === job.id ? "…" : "No-go"}
            </button>
            <button
              type="button"
              data-testid="jobs-all-start"
              onClick={(e) => handleStartPipeline(job.id, e)}
              disabled={creatingJobId === job.id}
              className="rounded-md bg-brand px-2.5 py-1 text-xs font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
            >
              {creatingJobId === job.id ? "…" : "Start"}
            </button>
          </div>
        );
      }
    }
    return null;
  };

  const columns = useMemo((): Column<Job>[] => {
    const cols: Column<Job>[] = [];
    if (tabAllowsSelect(activeTab)) {
      cols.push({
        key: "select",
        header: "",
        className: "w-10",
        render: (job) => renderSelectCheckbox(job),
      });
    }
    cols.push(
      {
        key: "company",
        header: "Company",
        sortable: true,
        render: (job) => (
          <span className="font-medium text-foreground">
            {displayCompany(job)}
          </span>
        ),
      },
      {
        key: "role",
        header: "Role",
        sortable: true,
        render: (job) => (
          <span className="text-muted-foreground">{displayRole(job)}</span>
        ),
      },
      {
        key: "source",
        header: "Source",
        sortable: true,
        render: (job) => (
          <>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-medium",
                sourceBadgeClass(job.source),
              )}
            >
              {sourceLabel(job.source)}
            </span>
            {job.sourceDetail ? (
              <span className="ml-1 text-[10px] text-muted-foreground">
                {job.sourceDetail}
              </span>
            ) : null}
          </>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (job) => {
          const pipeline = getPipelineForJob(job, pipelines);
          return (
            <span className="text-foreground">
              {activeTab === "nogo" || isNoGoJob(job)
                ? "No-go"
                : pipeline
                  ? pipeline.currentStage ?? "Pipeline"
                  : "Needs review"}
            </span>
          );
        },
      },
      {
        key: "score",
        header: "Score",
        sortable: true,
        render: (job) => {
          const displayScore = resolveJobScore(job);
          const tone =
            displayScore !== undefined ? scoreTone(String(displayScore)) : "muted";
          return displayScore !== undefined ? (
            <span
              className={cn(
                "inline-flex rounded-full px-2 py-0.5 font-mono text-xs",
                TONE_BG[tone],
              )}
            >
              {displayScore}/5
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      {
        key: "added",
        header: activeTab === "nogo" ? "Dismissed" : "Added",
        sortable: true,
        render: (job) => (
          <span className="text-muted-foreground">
            {new Date(
              activeTab === "nogo" ? job.updatedAt : jobAddedAt(job),
            ).toLocaleDateString()}
          </span>
        ),
      },
    );
    if (
      activeTab === "inbox" ||
      activeTab === "nogo" ||
      activeTab === "pipeline" ||
      activeTab === "all"
    ) {
      cols.push({
        key: "actions",
        header: "Actions",
        className: "text-right",
        render: (job) =>
          renderActions(job) ?? (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      });
    }
    return cols;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- actions close over mutation state
  }, [
    activeTab,
    pipelines,
    creatingJobId,
    dismissingJobId,
    restoringJobId,
    selectedIds,
  ]);

  const showSelectionBar =
    tabAllowsSelect(activeTab) && selectedIds.size > 0;

  return (
    <>
      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Jobs</h1>
            <p className="text-muted-foreground">
              Triage new roles by source — no-go or start a pipeline.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/explore"
              className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted/20"
            >
              Explore
            </Link>
            <Link
              to="/jobs/add"
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90"
            >
              + Add Job
            </Link>
          </div>
        </div>
      </header>

      <div className="mb-6 flex flex-wrap gap-1 border-b border-border">
        {tabs.map((tab) => {
          const glossaryKey = TAB_GLOSSARY[tab.id];
          const tip =
            tab.id === "all"
              ? "All jobs returned for this account (includes every bucket)."
              : JOB_BUCKET_GLOSSARY[glossaryKey];
          return (
            <button
              key={tab.id}
              type="button"
              title={tip}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition",
                activeTab === tab.id
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.label}
              <span
                title={tip}
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-xs",
                  activeTab === tab.id
                    ? "bg-brand/20 text-brand"
                    : "bg-muted/20 text-muted-foreground",
                )}
              >
                {tab.count}
              </span>
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand" />
              )}
            </button>
          );
        })}
      </div>

      {actionError && (
        <div
          className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-foreground"
          role="alert"
          data-testid="jobs-nogo-conflict"
        >
          <p>{actionError.message}</p>
          {actionError.pipelineId ? (
            <Link
              to={pipelineHref(actionError.pipelineId)}
              className="mt-2 inline-block text-sm font-medium text-brand hover:underline"
              data-testid="jobs-discard-in-pipeline"
            >
              Discard in Pipeline
            </Link>
          ) : null}
          <button
            type="button"
            className="ml-3 text-xs text-muted-foreground hover:underline"
            onClick={() => setActionError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {undoNoGoIds && undoNoGoIds.length > 0 && (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-brand/30 bg-brand/5 px-4 py-3 text-sm text-foreground"
          data-testid="jobs-bulk-nogo-undo"
        >
          <span>
            Marked {undoNoGoIds.length} no-go. Undo available for 15 seconds.
          </span>
          <button
            type="button"
            data-testid="jobs-bulk-nogo-undo-btn"
            onClick={runUndoBulkNoGo}
            disabled={bulkRestoreMutation.isPending}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
          >
            Undo
          </button>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:underline"
            onClick={clearUndoWindow}
          >
            Dismiss
          </button>
        </div>
      )}

      {bulkSummary && (
        <div className="mb-4 rounded-lg border border-border bg-muted/10 px-4 py-3 text-sm text-muted-foreground">
          {bulkSummary}
          <button
            type="button"
            className="ml-3 text-xs hover:underline"
            onClick={() => setBulkSummary(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="mb-4">
        <CollectionCommandBar
          searchQuery={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search company or role…"
          sortOption={sortOption}
          onSortChange={(s) => setSortOption(s as EntitySortOption<JobSortKey>)}
          sortOptions={JOB_SORT_OPTIONS}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          allowedModes={allowedModes}
          filterFields={filterFields}
          activeFilterCount={
            (sourceFilter ? 1 : 0) + (titleFitFilter ? 1 : 0)
          }
          onClear={() => {
            setSearch("");
            setSourceFilter("");
            setTitleFitFilter("");
          }}
        />
      </div>

      {activeTab === "inbox" && titleFitFilter && (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-3 py-2"
          data-testid="jobs-nogo-not-matching-row"
        >
          <p className="text-sm text-muted-foreground">
            Title fit filter active —{" "}
            {notMatchingTitleFitIds.length === 0
              ? "every inbox job matches."
              : `${notMatchingTitleFitIds.length} inbox job${
                  notMatchingTitleFitIds.length === 1 ? "" : "s"
                } do not match.`}
          </p>
          <button
            type="button"
            data-testid="jobs-nogo-not-matching"
            onClick={() => setConfirmNotMatchingOpen(true)}
            disabled={
              bulkDismissMutation.isPending ||
              notMatchingTitleFitIds.length === 0
            }
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/20 disabled:opacity-50"
            title={
              notMatchingTitleFitIds.length === 0
                ? "No inbox jobs outside this title-fit filter"
                : undefined
            }
          >
            No-go all not matching title fit ({notMatchingTitleFitIds.length})
          </button>
        </div>
      )}

      {activeTab === "inbox" &&
        inboxJobs.length > 0 &&
        selectedIds.size === 0 && (
          <p
            className="mb-4 text-xs text-muted-foreground"
            data-testid="jobs-bulk-select-hint"
          >
            Select rows, then Bulk No-go or Bulk Start.
          </p>
        )}

      {showSelectionBar && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-3 py-2">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={allPagedSelected}
              onChange={toggleSelectAllPaged}
              className="h-4 w-4 accent-brand"
              data-testid="jobs-select-all-page"
            />
            {selectedIds.size} selected
          </label>
          {(activeTab === "inbox" || activeTab === "all") && (
            <>
              <button
                type="button"
                data-testid="jobs-bulk-nogo"
                onClick={() => setConfirmBulkNoGoOpen(true)}
                disabled={bulkDismissMutation.isPending || selectedInboxCount === 0}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/20 disabled:opacity-50"
              >
                Bulk No-go
                {activeTab === "all" ? ` (${selectedInboxCount})` : ""}
              </button>
              {activeTab === "inbox" && (
                <button
                  type="button"
                  data-testid="jobs-bulk-start"
                  onClick={() => setConfirmBulkStartOpen(true)}
                  disabled={bulkStartBusy || selectedInboxCount === 0}
                  className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
                >
                  Bulk Start
                </button>
              )}
            </>
          )}
          {activeTab === "nogo" && (
            <button
              type="button"
              data-testid="jobs-bulk-restore"
              onClick={() => setConfirmBulkRestoreOpen(true)}
              disabled={bulkRestoreMutation.isPending}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/20 disabled:opacity-50"
            >
              Bulk Restore
            </button>
          )}
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-muted-foreground hover:underline"
          >
            Clear selection
          </button>
        </div>
      )}

      {activeTab === "pipeline" && (
        <p className="mb-4 text-xs text-muted-foreground">
          Manage stages on the{" "}
          <Link to="/pipeline" className="text-brand hover:underline">
            Pipeline page
          </Link>
          .
        </p>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}

      {!isLoading && filteredJobs.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            {search || sourceFilter || titleFitFilter
              ? "No jobs match your filters."
              : activeTab === "inbox"
                ? "Inbox clear. Explore new roles or import a batch."
                : activeTab === "pipeline"
                  ? "No active pipelines. Start one from the Inbox tab."
                  : activeTab === "nogo"
                    ? "No dismissed jobs."
                    : "No jobs yet."}
          </p>
          {activeTab === "inbox" && !search && !sourceFilter && !titleFitFilter && (
            <Link
              to="/explore"
              className="mt-4 inline-block rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground"
            >
              Explore roles
            </Link>
          )}
        </div>
      )}

      {!isLoading && pagedJobs.length > 0 && (
        <>
          <EntityListLayout
            viewMode={viewMode}
            table={
              <DataTable
                data={sortedItems}
                columns={columns}
                onRowClick={(job) => navigate(`/jobs/${job.id}`)}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            }
            list={
              <div className="space-y-2">
                {pagedJobs.map((job) => {
                  const displayScore = resolveJobScore(job);
                  return (
                    <EntityCompactItem
                      key={job.id}
                      id={job.id}
                      title={displayCompany(job)}
                      description={displayRole(job)}
                      badges={[
                        {
                          label: sourceLabel(job.source),
                          colorClass: sourceBadgeClass(job.source),
                        },
                        ...(displayScore !== undefined
                          ? [
                              {
                                label: `${displayScore}/5`,
                                colorClass:
                                  TONE_BG[scoreTone(String(displayScore))],
                              },
                            ]
                          : []),
                      ]}
                      actions={
                        <div className="flex items-center gap-2">
                          {tabAllowsSelect(activeTab)
                            ? renderSelectCheckbox(job)
                            : null}
                          {renderActions(job)}
                        </div>
                      }
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    />
                  );
                })}
              </div>
            }
            cards={
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {pagedJobs.map((job) => {
                  const displayScore = resolveJobScore(job);
                  return (
                    <EntityCard
                      key={job.id}
                      id={job.id}
                      title={displayCompany(job)}
                      description={displayRole(job)}
                      primaryBadge={{
                        label: sourceLabel(job.source),
                        colorClass: sourceBadgeClass(job.source),
                      }}
                      secondaryBadge={
                        displayScore !== undefined
                          ? {
                              label: `${displayScore}/5`,
                              colorClass:
                                TONE_BG[scoreTone(String(displayScore))],
                            }
                          : undefined
                      }
                      metadata={[
                        {
                          value: new Date(
                            activeTab === "nogo"
                              ? job.updatedAt
                              : jobAddedAt(job),
                          ).toLocaleDateString(),
                        },
                      ]}
                      actions={
                        <div className="flex items-center gap-2">
                          {tabAllowsSelect(activeTab)
                            ? renderSelectCheckbox(job)
                            : null}
                          {renderActions(job)}
                        </div>
                      }
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    />
                  );
                })}
              </div>
            }
          />
          {useServerCursor ? (
            <PaginationBar
              mode="cursor"
              page={cursorPag.page}
              pageSize={cursorPag.pageSize}
              itemCount={pagedJobs.length}
              showingFrom={
                pagedJobs.length === 0 ? 0 : cursorPag.showingFrom
              }
              showingTo={cursorPag.showingTo(pagedJobs.length)}
              hasMore={cursorPag.hasMore}
              canPrev={cursorPag.canPrev}
              onFirst={cursorPag.goFirst}
              onPrev={cursorPag.goPrev}
              onNext={cursorPag.goNext}
              onPageSizeChange={cursorPag.setPageSize}
            />
          ) : (
            <PaginationBar
              page={clientPag.page}
              pageSize={clientPag.pageSize}
              totalItems={filteredJobs.length}
              totalPages={clientPag.totalPages}
              showingFrom={clientPag.showingFrom}
              showingTo={clientPag.showingTo}
              onPageChange={clientPag.setPage}
              onPageSizeChange={clientPag.setPageSize}
            />
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmNoGoJobId !== null}
        title="Mark as no-go?"
        description="This job will leave your inbox and Ready list."
        confirmLabel="No-go"
        confirmTone="danger"
        busy={dismissingJobId !== null}
        testId="jobs-nogo-confirm"
        onCancel={() => setConfirmNoGoJobId(null)}
        onConfirm={() => {
          if (!confirmNoGoJobId) return;
          const id = confirmNoGoJobId;
          setConfirmNoGoJobId(null);
          runNoGo(id);
        }}
      />

      <ConfirmDialog
        open={confirmBulkNoGoOpen}
        title={`No-go ${activeTab === "all" ? selectedInboxCount : selectedIds.size} job${(activeTab === "all" ? selectedInboxCount : selectedIds.size) === 1 ? "" : "s"}?`}
        description="Selected jobs leave Inbox and Ready. Jobs already in a pipeline are skipped."
        confirmLabel="Bulk No-go"
        confirmTone="danger"
        busy={bulkDismissMutation.isPending}
        testId="jobs-bulk-nogo-confirm"
        onCancel={() => setConfirmBulkNoGoOpen(false)}
        onConfirm={runBulkNoGoSelected}
      />

      <ConfirmDialog
        open={confirmBulkStartOpen}
        title={`Start pipeline for up to ${Math.min(selectedInboxCount, BULK_START_CAP)} job${Math.min(selectedInboxCount, BULK_START_CAP) === 1 ? "" : "s"}?`}
        description={`Creates pipelines for selected Inbox jobs (max ${BULK_START_CAP} per run).`}
        confirmLabel="Bulk Start"
        busy={bulkStartBusy}
        testId="jobs-bulk-start-confirm"
        onCancel={() => setConfirmBulkStartOpen(false)}
        onConfirm={() => void runBulkStart()}
      />

      <ConfirmDialog
        open={confirmBulkRestoreOpen}
        title={`Restore ${selectedIds.size} job${selectedIds.size === 1 ? "" : "s"} to inbox?`}
        description="Selected No-go jobs return to Inbox / Ready."
        confirmLabel="Bulk Restore"
        busy={bulkRestoreMutation.isPending}
        testId="jobs-bulk-restore-confirm"
        onCancel={() => setConfirmBulkRestoreOpen(false)}
        onConfirm={runBulkRestore}
      />

      <ConfirmDialog
        open={confirmNotMatchingOpen}
        title={`No-go ${notMatchingTitleFitIds.length} job${notMatchingTitleFitIds.length === 1 ? "" : "s"} not matching filter?`}
        description="Marks Inbox jobs that do not match the current title-fit filter as no-go. Undo available for 15 seconds."
        confirmLabel="No-go not matching"
        confirmTone="danger"
        busy={bulkDismissMutation.isPending}
        testId="jobs-nogo-not-matching-confirm"
        onCancel={() => setConfirmNotMatchingOpen(false)}
        onConfirm={runBulkNoGoNotMatching}
      />
    </>
  );
}
